/**
 * Role-based access control (RBAC) utility functions
 */

/**
 * Check if a user has any of the required roles
 * @param {Array} userRoles - Array of roles the user has
 * @param {Array} requiredRoles - Array of roles required for access
 * @returns {boolean} - True if user has at least one of the required roles
 */
export const hasRequiredRole = (userRoles = [], requiredRoles = []) => {
  // If no roles are required, allow access
  if (!requiredRoles.length) return true;

  // If roles are required but user has none, deny access
  if (!userRoles.length) return false;

  // Check if user has any of the required roles
  return requiredRoles.some((role) => userRoles.includes(role));
};

/**
 * Available roles in the application
 */
export const ROLES = {
  ADMIN: 'Administrator',
  MODERATOR: 'Moderator',
  HEDGE_DESK: 'HedgeDesk',
  ORIGINATOR: 'Originator',
};

/**
 * Role configurations for different routes/features
 */
export const ROUTE_ROLES = {
  '/admin': [ROLES.ADMIN],
  '/neworder': [ROLES.ADMIN, ROLES.HEDGE_DESK, ROLES.ORIGINATOR],
  '/hedging': [ROLES.ADMIN, ROLES.HEDGE_DESK],
  '/originator': [ROLES.ADMIN, ROLES.ORIGINATOR],
};

/**
 * Check if a user has access to a specific route
 * @param {string} path - Route path to check
 * @param {Array} userRoles - Array of user roles
 * @returns {boolean} - True if user has access to the route
 */
export const canAccessRoute = (path, userRoles = []) => {
  const requiredRoles = ROUTE_ROLES[path];
  if (!requiredRoles) return true; // No specific roles required for this route
  return hasRequiredRole(userRoles, requiredRoles);
};
